// Shared primitives (E01-S1-T1). Every schema in this package is built from these.
//
// The one rule that matters most: objects are STRICT. zod's default object mode strips unknown
// keys silently, which would turn a leaked raw value into a dropped key that nobody sees. So the
// package never uses z.object or .passthrough; it uses strictObject, which rejects an unknown key
// and names it. test/primitives.test.ts walks the export list to enforce this.
import { z } from "zod";
/** A zod object that rejects unknown keys. The only object constructor this package uses. */
export const strictObject = z.strictObject;
/** The mandatory `v` literal every top-level contract object carries. */
export const version = (n) => z.literal(n);
/** ULID: 26 Crockford base32 characters, as contracts 1.0 says ids are unless a format is given. */
export const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;
export const Ulid = z.string().regex(ULID_RE, "expected a ULID (26 Crockford base32 characters)");
/**
 * ISO-8601 UTC: date, time, optional fraction, and a literal Z. A local time, an offset (+01:00)
 * or a bare date fails. The calendar value must also be real (2026-02-30 fails).
 */
export const ISO_UTC_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d{1,9})?Z$/;
export const IsoUtc = z
    .string()
    .regex(ISO_UTC_RE, "expected ISO-8601 UTC ending in Z")
    .refine((s) => {
    const m = ISO_UTC_RE.exec(s);
    if (!m)
        return false;
    const [, y, mo, d, h, mi, se] = m;
    const t = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(se));
    if (Number.isNaN(t))
        return false;
    const back = new Date(t);
    return (back.getUTCFullYear() === Number(y) &&
        back.getUTCMonth() === Number(mo) - 1 &&
        back.getUTCDate() === Number(d) &&
        back.getUTCHours() === Number(h) &&
        back.getUTCMinutes() === Number(mi) &&
        back.getUTCSeconds() === Number(se));
}, "not a real UTC instant");
/** Perception tiers: 0 route · 1 structure · 2 values · 3 pixels. */
export const PerceptionTier = z.literal([0, 1, 2, 3]);
/** The class of an atlas field; the class decides what leaves the device. */
export const FieldClass = z.enum([
    "structural",
    "enum",
    "numeric",
    "identifier",
    "pii",
    "free-text",
]);
/** A non-empty, trimmed string — ids, titles, authored text. */
export const NonEmpty = z.string().trim().min(1);
/** A non-negative integer: counts, revisions, sequence numbers, byte lengths. */
export const Count = z.number().int().nonnegative();
/** A stable identifier in the pack's own vocabulary: screen, field, target, signal, step ids. */
export const ID_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
export const Id = z
    .string()
    .regex(ID_RE, "expected an id: letters, digits, dot, underscore or dash");
/** Field, collection and context ids addressed by a reference (D-04): a letter, then letters, digits, _ or -. No dots. */
export const REF_ID_RE = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;
export const RefId = z
    .string()
    .regex(REF_ID_RE, "expected an id a reference can address: a letter, then letters, digits, _ or -");
/** Screen context keys, addressed by `$screen.context.<key>`: the same grammar. */
export const CONTEXT_KEY_RE = REF_ID_RE;
export const ContextKey = z
    .string()
    .regex(CONTEXT_KEY_RE, "expected a context key: a letter, then letters, digits, _ or -");
/**
 * A secret REFERENCE, never a value: a closed scheme and a name with no `@`, `/` runs or spaces, so a
 * connection string or a key pasted where a reference belongs is refused instead of printed.
 */
export const SECRET_REF_SCHEMES = ["env", "file", "gcp-sm", "k8s", "vault"];
export const SECRET_REF_RE = /^(env|file|gcp-sm|k8s|vault):\/?[A-Za-z0-9_][A-Za-z0-9_.-]{0,127}(\/[A-Za-z0-9_.-]{1,64}){0,8}$/;
export const SecretRef = z
    .string()
    .regex(SECRET_REF_RE, "expected a secret reference such as env:OPENROUTER_API_KEY, never a value");
//# sourceMappingURL=primitives.js.map